import { PanelSegmentedFilter } from "@/components/layout/SplitPanel/PanelSegmentedFilter.tsx";
import {
  PanelToolsSearch,
  type PanelToolsSearchProps,
} from "@/components/layout/SplitPanel/PanelToolsSearch.tsx";
import { panelToolbarControlClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import { SlidersHorizontal } from "lucide-react";
import {
  hasAppliedFilters,
  hasMenuControls,
  PanelControls,
  splitPanelControls,
  type PanelControlAxes,
} from "@/views/Settings/panelControls.tsx";
import {
  panelDescriptionClass,
  type SettingsPanelAction,
} from "@/views/Settings/SettingsPanelHeader.tsx";
import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";

/**
 * Which half of a phone's row opens at full size: the search field, or the
 * filter axes spelled out.
 *
 * A wide row shows both, so this only ever decides a phone. It is the page's
 * call because it is really a statement about what the screen is for: the
 * Search screen opens on its field, since a search is what brought you there,
 * while a list you came to browse opens on the axes that narrow it.
 */
export type MobileFocus = "search" | "filters";

/**
 * Below this, the row cannot seat an open search field and a spelled-out axis at
 * once: the field alone asks for 264px, and what is left has to carry at least a
 * couple of options, the menu glyph and the page's action.
 */
const ROW_FOLD_PX = 600;

/**
 * Whether the row is too narrow to show both halves at full size.
 *
 * Measured from the row itself rather than the viewport. These pages sit in a
 * canvas column the reader can drag down to a few hundred pixels while the
 * window stays wide, and a viewport breakpoint calls that column "desktop" and
 * leaves both halves fighting over a row neither fits in.
 */
const useFoldedRow = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [folded, setFolded] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // A zero width is "not laid out yet", not "narrower than anything" — an
    // unmeasured row folding would flash the glyphs on every mount, and would
    // make every test that never lays anything out assert against the folded
    // arrangement. A boolean, so an unchanged measurement bails out of render.
    const measure = () => {
      const width = el.clientWidth;
      setFolded(width > 0 && width < ROW_FOLD_PX);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, folded };
};

/**
 * The filter axes, folded down to the glyph that brings them back.
 *
 * Stands in for the segment row while the field has the phone's row, so the
 * axes are one tap away rather than gone — and lights when one of them is
 * narrowing the list, which is the only thing left saying so once the words
 * themselves are folded away.
 *
 * Sliders rather than the funnel `triggerIntent` picks: when a filter stays in
 * the menu, that trigger sits directly beside this one, and two identical
 * funnels an inch apart say nothing about which opens what. The name is distinct
 * for the same reason. Carries no `aria-expanded`: the row it opens replaces it,
 * so there is no expanded state to report and nothing to press again.
 */
const FilterToggle = ({
  subject,
  applied,
  statsigName,
  onClick,
}: {
  subject: string;
  applied: boolean;
  statsigName: string;
  onClick: () => void;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn("shrink-0", panelToolbarControlClass)}
        aria-label={`Show ${subject} filters`}
        data-statsig-name={`${statsigName}-expand`}
        onClick={onClick}
      >
        <SlidersHorizontal className={cn(applied && "text-primary")} />
      </Button>
    </TooltipTrigger>
    <TooltipContent>{`Show ${subject} filters`}</TooltipContent>
  </Tooltip>
);

/**
 * A page's own actions, on a row of their own between the title and the list.
 *
 * A page's filters are the screen's business, not the app's chrome, so they sit
 * in the content column, directly above the list they change and at the width
 * that list is drawn at — where the eye already is when it wonders what it is
 * looking at.
 *
 * The row has two halves and they answer different questions. On the left, the
 * axes a page has promoted are spelled out in full, so *what am I looking at*
 * is legible without opening anything. On the right, the controls that only
 * matter once you are looking — search, whatever axes stayed in the menu, and
 * the page's own actions — stay compact, because spelling those out too would
 * leave no room for the half that needed the width.
 *
 * Which axes go left is the page's call, declared per axis; see
 * `PanelControlDimension.display`.
 */
export const PanelToolbar = ({
  controls,
  search,
  subject,
  statsigName,
  description,
  mobileFocus = "filters",
  trailing,
  action,
}: {
  controls: PanelControlAxes;
  /**
   * Omitted by a page that has nothing to search, which is its own statement.
   *
   * Without the four props this row decides for every page — whether the field
   * stands open, what closes it, whether it takes the row, and what size it is
   * drawn at — so a page cannot set one and be silently overruled here.
   */
  search?: Omit<
    PanelToolsSearchProps,
    "expanded" | "controlClassName" | "onOpenChange" | "fillRow" | "focusOnMount"
  >;
  /** What the page lists, e.g. "routines" — the filter menu's accessible name. */
  subject: string;
  /** Base analytics name for the filter trigger, e.g. "routines-canvas-filter". */
  statsigName: string;
  /**
   * One line saying what this view holds, shown under the row while the list is
   * showing everything.
   *
   * It introduces the page, and once the reader has narrowed the list they have
   * told you they know what the page is for — so it stands down and gives the
   * room to the results. Typing a query counts as narrowing just as picking a
   * filter does: a sentence promising everything the page holds, sitting over
   * two search results, is no longer true. Sorting or switching between rows and
   * cards changes neither what is shown nor how much, and leaves it in place.
   *
   * Under the row rather than above it so the controls keep their position when
   * it goes: a description above would drag the whole toolbar up the page on the
   * first filter click, moving the control that was just used.
   */
  description?: string;
  /**
   * Which half opens at full size on a phone. Defaults to the filters: most of
   * these screens are lists you came to browse, and the axes are what narrow
   * them. See {@link MobileFocus}.
   */
  mobileFocus?: MobileFocus;
  /**
   * A control of the page's own, at the end of the axes.
   *
   * For a standing preference about the page rather than something that
   * narrows the list — Tasks puts what okthx skips here. It sits with the menu
   * glyph instead of the title because that is where a reader of these pages
   * already looks for a control, and markup rather than ingredients because
   * each one is its own popover; there is no shape for them to share yet.
   */
  trailing?: ReactNode;
  /**
   * The page's primary action, e.g. "New routine". Sits last, on the trailing
   * edge.
   *
   * Ingredients rather than markup, and the same `SettingsPanelAction` the
   * settings chrome takes — so the phone treatment below is decided once here
   * instead of by each page remembering to hide its own label.
   */
  action?: SettingsPanelAction;
}) => {
  const { segmented, menu } = splitPanelControls(controls);
  const { ref: rowRef, folded } = useFoldedRow();
  // A phone's row seats one of the two at full size, never both: a field and a
  // spelled-out axis side by side leave each a sliver. Which one starts there is
  // the page's call — see `mobileFocus` — and tapping the other's glyph trades
  // places. A wide row has space for both, so neither ever collapses there.
  const [focus, setFocus] = useState<MobileFocus>(mobileFocus);
  const searchExpanded = !folded || focus === "search";
  const filtersExpanded = !folded || focus === "filters";
  // Scoped to the folded half, because that is what the glyph stands for. Fed
  // the whole set it would light for a menu filter as well, and tapping it
  // would unfold a row sitting at its default — a lit control opening nothing.
  const segmentedApplied = hasAppliedFilters({ filters: segmented });
  // A query narrows the list exactly as a filter does, so the description stands
  // down for either. Read from the controlled value rather than from whether the
  // field is open: an empty field standing open on a wide row is the resting
  // state, not a search.
  const narrowed = hasAppliedFilters(controls) || Boolean(search?.value);
  const showsDescription = Boolean(description) && !narrowed;

  return (
    <>
      {/* One row of siblings, in the order search → filters → the rest. Whichever
          of the first two is expanded takes the slack and the other sits as a
          glyph, so the same markup draws both arrangements and the controls keep
          their left-to-right order across the swap: what was a glyph on the left
          is still on the left once it opens.

          Stays put while the list moves under it. The whole point of leaving the
          header was that these controls should be where the eye already is, and
          a row that scrolls away one screenful down is hidden again by a
          different route. `bg-background` because the cards pass underneath it.

          `mb-4` stands in for the description's own bottom margin when there is
          no description to carry it. With the row's `py-2` that is the same 24px
          the sentence leaves under itself, so what follows sits the same
          distance below the controls whether the list is filtered or not. */}
      <div
        ref={rowRef}
        className={cn(
          "sticky top-0 z-10 flex items-center gap-2 bg-background py-2",
          !showsDescription && "mb-4",
        )}
      >
        {/* Ahead of the filters: search is the widest net a page casts, and the
            axes beside it narrow what it returns. Reading left to right that is
            the order the two are used in. */}
        {search ? (
          <PanelToolsSearch
            {...search}
            expanded={searchExpanded}
            fillRow={folded}
            // Only the page that came for search takes the caret on arrival.
            focusOnMount={mobileFocus === "search"}
            controlClassName={panelToolbarControlClass}
            // Opening the field on a phone is what hands it the row; letting it
            // go hands the row back to whichever half the page opens on, so the
            // Search screen keeps its field rather than losing it to a stray tap.
            onOpenChange={(open) => setFocus(open ? "search" : mobileFocus)}
          />
        ) : null}
        {/* `min-w-0` so a long segment row scrolls inside itself instead of
            pushing the controls beside it past the gutter. `flex-1` lets it take
            whatever the rest of the row does not need. */}
        {filtersExpanded ? (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {segmented.map((axis, index) => (
              <Fragment key={axis.statsigName}>
                {/* Between axes only. Two promoted axes are two separate questions
                — a type and a scope, say — and without a rule between them the
                row reads as one long list of options where the fill jumps
                twice for reasons the eye cannot group. */}
                {index > 0 ? <Separator orientation="vertical" className="h-5 shrink-0" /> : null}
                <PanelSegmentedFilter axis={axis} />
              </Fragment>
            ))}
          </div>
        ) : (
          <FilterToggle
            subject={subject}
            applied={segmentedApplied}
            statsigName={statsigName}
            onClick={() => setFocus("filters")}
          />
        )}
        <div className="flex shrink-0 items-center gap-1">
          {/* Before the menu: the axes and this are both about the list, and a
              page control sitting between them would split the pair. */}
          {trailing}
          {/* Dropped entirely when every axis was promoted and the page has no
            sort or view: a glyph whose menu is empty invites a click that
            answers nothing. */}
          {hasMenuControls(menu) ? (
            <PanelControls
              {...menu}
              subject={subject}
              statsigName={statsigName}
              triggerClassName={panelToolbarControlClass}
            />
          ) : null}
          {action ? (
            <Button
              size="fit"
              variant="primary"
              onClick={action.onClick}
              // Carried always, because the label is gone on a phone and the glyph
              // alone names nothing.
              aria-label={action.label}
              data-statsig-name={action.statsigName}
              // One height on every screen, like everything else on this row. The
              // label is what goes on a phone, where the row's width belongs to
              // the segments and a spelled-out action would leave them two
              // options wide.
              // `[&_svg]:size-4` because `Button`'s base sets no glyph size, so a
              // lucide icon falls back to its own 24px and overflows this 28px
              // box. The icon-only controls get theirs from
              // `panelToolbarControlClass`; this one carries its own sizing and
              // so has to say it.
              className="h-7 shrink-0 px-2.5 text-xs [&_svg]:size-4 max-md:w-7 max-md:px-0"
            >
              <action.icon />
              <span className="hidden md:inline">{action.label}</span>
            </Button>
          ) : null}
        </div>
      </div>
      {showsDescription ? (
        // `mt-4` on top of the row's own `py-2` puts 24px between the segments
        // and this line, matching the 24px `panelDescriptionClass` already
        // leaves under it — so the sentence sits centred in its gap rather than
        // crowding the controls it follows. Applied here rather than in the
        // shared class, which the settings panels lay out differently.
        <p className={cn(panelDescriptionClass, "mt-4")}>{description}</p>
      ) : null}
    </>
  );
};
